/**
 * Open new FI-operator case (/admin/support/new)
 *
 * FI-operator audience only. Posts to POST /api/v1/support/fi-cases with
 * a SupportCaseFiOpenRequest body. The tenantId is derived server-side
 * via canonical resolveEffectiveTenantId; the SPA does NOT send it.
 *
 * Anyone else (platform-admin without an FI-operator role, anonymous, or
 * consumer) lands on a 403 fallback panel directing them to /admin/support.
 *
 * No top-level view-model interfaces; only direct schema aliases are
 * declared as named types.
 */

import { useCallback, useMemo, useState } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import {
  BodyText,
  Button,
  Card,
  Input,
  Textarea,
  Select,
  Alert,
} from '@aaimplatform/ui';
import type { components } from '@aaimplatform/institutional-types/support-platform';
import { useAuthStore } from '../../stores/auth';
import { supportPlatformClient } from '../../api/client';
import { SupportServiceMissingError, SupportServiceNotice, supportFailure } from './SupportServiceNotice';

type SupportCasePriority = components['schemas']['SupportCasePriority'];

const FI_OPERATOR_ROLES = [
  'fi_admin',
  'fi_loan_officer',
  'fi_compliance',
] as const;

export function SupportNewCasePage() {
  const navigate = useNavigate();
  // GH #1962: the portal's former dead-ends -- the ones that told an operator to
  // email someone -- now route HERE with the request pre-scoped: which page, which
  // tenant, which field. The operator lands on a real form, already filled in,
  // that files a real case. They do not land in a mail client.
  const [searchParams] = useSearchParams();
  const user = useAuthStore((s) => s.user);
  const roles = useMemo(
    () => (user?.roles ?? []).map((r) => (typeof r === 'string' ? r.toLowerCase() : r)),
    [user],
  );
  const isFiOperator = FI_OPERATOR_ROLES.some((r) => roles.includes(r));

  const [subject, setSubject] = useState<string>(() => searchParams.get('subject') ?? '');
  const [description, setDescription] = useState<string>(() => searchParams.get('description') ?? '');
  const [slaTier, setSlaTier] = useState<string>('P3');
  const [posting, setPosting] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);
  const [serviceMissing, setServiceMissing] = useState<boolean>(false);

  const handleSubmit = useCallback(async () => {
    if (!isFiOperator) return;
    setServiceMissing(false);
    if (!subject.trim() || !description.trim()) {
      setError('Subject and description are required.');
      return;
    }
    setPosting(true);
    setError(null);
    try {
      const { data, error: apiError, response } = await supportPlatformClient.POST(
        '/api/v1/support/fi-cases',
        {
          body: {
            subject,
            description,
            slaTier: slaTier ? (slaTier as SupportCasePriority) : undefined,
          },
        },
      );
      if (apiError !== undefined || !response.ok) throw supportFailure(response, apiError, 'Failed to open case');
      if (data?.id) {
        navigate(`/admin/support/${data.id}`);
      } else {
        navigate('/admin/support');
      }
    } catch (err) {
      if (err instanceof SupportServiceMissingError) setServiceMissing(true);
      else setError(err instanceof Error ? err.message : 'Failed to open case');
    } finally {
      setPosting(false);
    }
  }, [isFiOperator, subject, description, slaTier, navigate]);

  if (!isFiOperator) {
    return (
      <div className="p-6 space-y-4">
        {/* Page title lives in the sticky topnav (AppHeader); no redundant H1. */}
        <Card>
          <div className="p-4 space-y-3">
            <Alert type="error">
              Opening a support case is restricted to staff at your institution with
              the FI Admin, FI Loan Officer or FI Compliance role.
            </Alert>
            <Link to="/admin/support" className="text-primary hover:underline text-sm">
              &larr; Back to cases
            </Link>
          </div>
        </Card>
      </div>
    );
  }

  const slaOptions = [
    { value: 'P1', label: 'P1 (critical)' },
    { value: 'P2', label: 'P2 (high)' },
    { value: 'P3', label: 'P3 (normal)' },
    { value: 'P4', label: 'P4 (low)' },
  ];

  return (
    <div className="p-6 space-y-4">
      <Link to="/admin/support" className="text-primary hover:underline text-sm">
        &larr; Back to cases
      </Link>
      {/* Page title lives in the sticky topnav (AppHeader); the body opens
          with the descriptive subtitle, no redundant H1. */}
      <BodyText size="sm" color="secondary">
        Opens a support case for your institution. Aaim support will receive the
        case and respond via the message thread on the case detail page.
      </BodyText>

      <Card>
        <div className="p-4 space-y-3">
          {serviceMissing && <SupportServiceNotice />}
          {error && <Alert type="error">{error}</Alert>}
          <Input
            label="Subject"
            value={subject}
            onChange={(e) => setSubject(e.target.value)}
            maxLength={500}
            required
            placeholder="One-line summary of the issue or question"
          />
          <Textarea
            label="Description"
            value={description}
            onChange={(v) => setDescription(v)}
            rows={8}
            required
            placeholder="Describe the issue or question in detail."
          />
          <Select
            label="Priority"
            options={slaOptions}
            value={slaTier}
            onChange={(e) => setSlaTier(e.target.value)}
            helperText="P1 is reserved for critical / blocking issues."
          />
          <div className="flex justify-end">
            <Button
              variant="primary"
              onClick={handleSubmit}
              disabled={posting || !subject.trim() || !description.trim()}
            >
              {posting ? 'Opening...' : 'Open case'}
            </Button>
          </div>
        </div>
      </Card>
    </div>
  );
}
